// src/services/adminHttp.js
//
// Couche HTTP commune pour tous les services Admin.
// Interface Admin → Worker Admin uniquement (jamais un Worker métier direct).
//
// Responsabilités :
// - Authorization: Bearer (session Admin)
// - 401 → purge token + redirection /login
// - 403 → permission insuffisante
// - erreurs HTTP
// - HTTP 200 + { "erreur": "..." } → erreur métier

import { ADMIN_WORKER_URL, clearAdminLocalSession } from '../config/api.config';

const API_BASE_URL = ADMIN_WORKER_URL;

/**
 * Fetch authentifié vers le Worker Admin.
 * @param {string} endpoint chemin relatif (ex. "/admin/paiements")
 * @param {RequestInit} [options]
 */
export async function apiFetch(endpoint, options = {}) {
  const token = localStorage.getItem('dsi_admin_token');

  // acceptPartial : pour les opérations origins (200/207/502/503 avec corps utile)
  const { acceptPartial, ...restOptions } = options;

  const headers = {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...restOptions.headers,
  };

  const response = await fetch(`${API_BASE_URL}${endpoint}`, {
    ...restOptions,
    headers,
  });

  if (response.status === 401) {
    clearAdminLocalSession();
    window.location.href = '/login';
    throw new Error('Session expirée. Veuillez vous reconnecter.');
  }

  if (response.status === 403) {
    const forbidden = new Error('Accès refusé : Permission insuffisante');
    forbidden.status = 403;
    throw forbidden;
  }

  const data = await response.json().catch(() => ({}));

  // Propagation CORS : 207 partiel, 502 échec total, 503 aucune cible — corps utile
  if (
    acceptPartial &&
    (response.status === 207 ||
      response.status === 502 ||
      response.status === 503)
  ) {
    return {
      ...(data && typeof data === 'object' ? data : {}),
      _httpStatus: response.status,
    };
  }

  if (!response.ok) {
    const message =
      data.error || data.erreur || `Erreur serveur (${response.status})`;
    const err = new Error(message);
    err.status = response.status;
    err.body = data;
    throw err;
  }

  if (data && typeof data === 'object' && data.erreur) {
    const err = new Error(
      typeof data.erreur === 'string' ? data.erreur : 'Erreur métier'
    );
    err.status = 200;
    err.body = data;
    err.metier = true;
    throw err;
  }

  if (data && typeof data === 'object') {
    return { ...data, _httpStatus: response.status };
  }
  return data;
}

/** Construit une query string en ignorant les valeurs undefined / null / vides */
export function buildQuery(params = {}) {
  const usable = Object.entries(params).filter(
    ([, v]) => v !== undefined && v !== null && v !== ''
  );
  if (usable.length === 0) return '';
  return `?${new URLSearchParams(usable).toString()}`;
}
